import { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../config/axios';
import { authService } from '../services/authService';

interface UserProfile {
  name: string;                      // Contact person name
  specialty: string;                 // Business/medical specialty
  role: string;                      // Role in organization
  practice_size: string;             // Organization size
  email: string;
  profile_type: string;              // developer, regular, etc.
  user_type?: string;                // organization, individual, etc.
  organization_name?: string;        // Company name
  company_name?: string;             // Fallback company name
  subscription_status: string;
  recording_limits?: {
    daily_limit: number;
    total_limit: number;
    max_duration: number;
    daily_used: number;
    total_used: number;
    daily_remaining: number;
    total_remaining: number;
    unlimited: boolean;
    can_record: boolean;
    is_paid: boolean;
    subscription_status: string;
    plan_name: string;
  };
  subscription_details?: {
    status: string;
    plan_name: string;
    is_trial: boolean;
    trial_end: string;
    trial_remaining_days: number;
  };
}

export const useUserProfile = () => {
  // Initialize with cached data if available
  const [profile, setProfile] = useState<UserProfile | null>(() => {
    try {
      const cachedData = localStorage.getItem('user_profile');
      if (cachedData) {
        console.log('useUserProfile - Initializing with cached data:', cachedData);
        return JSON.parse(cachedData);
      }
    } catch (e) {
      console.error('Error parsing cached profile data:', e);
    }
    return null;
  });

  const [isLoading, setIsLoading] = useState<boolean>(!profile); // Only loading if no cached data
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  // Use a ref to track if we're already fetching
  const isFetchingRef = useRef(false);
  // Cache refresh interval in milliseconds (5 minutes)
  const REFRESH_INTERVAL = 5 * 60 * 1000;
  // Minimum time between manual refreshes to prevent abuse (10 seconds)
  const MANUAL_REFRESH_COOLDOWN = 10 * 1000;

  const fetchProfile = useCallback(async (force = false) => {
    // Skip if already fetching
    if (isFetchingRef.current) {
      console.log('useUserProfile - Already fetching, skipping');
      return;
    }

    // Check if we should skip the fetch due to recent fetch unless forcing
    const now = Date.now();
    if (!force && now - lastFetchTime < MANUAL_REFRESH_COOLDOWN) {
      console.log('useUserProfile - Recent fetch, skipping');
      return;
    }

    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);

      const token = authService.getToken();

      if (!token) {
        console.log('useUserProfile - No auth token found');
        setProfile(null);
        return;
      }

      const response = await axios.get('/api/scribing/user-profile/me', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log('useUserProfile - Fetched profile data:', response.data);
      setProfile(response.data);
      setLastFetchTime(now);

      // Save to localStorage for backup/caching
      localStorage.setItem('user_profile', JSON.stringify(response.data));
    } catch (error: any) {
      console.error('Error fetching user profile:', error);

      // If API call fails, try to use cached data from localStorage
      try {
        const cachedData = localStorage.getItem('user_profile');
        if (cachedData) {
          console.log('useUserProfile - Using cached profile data');
          setProfile(JSON.parse(cachedData));
        } else {
          setProfile(null);
        }
      } catch (e) {
        console.error('Error parsing cached profile data:', e);
        setProfile(null);
      }

      setError(error.message || 'Failed to fetch user profile');
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [lastFetchTime]);

  // Only set up the effect once and don't include fetchProfile in deps
  useEffect(() => {
    // Initial fetch
    fetchProfile(true);

    // Set up periodic refresh - fetch every 5 minutes
    const refreshInterval = setInterval(() => {
      console.log('useUserProfile - Scheduled refresh of profile data');
      fetchProfile(true);
    }, REFRESH_INTERVAL);

    return () => {
      clearInterval(refreshInterval);
    };
  }, []); // Empty dependency array to run only once

  // Expose a memoized refresh function that will force a refresh
  const refreshProfile = useCallback(() => {
    console.log('useUserProfile - Manual refresh triggered');
    return fetchProfile(true);
  }, [fetchProfile]);

  // Helper computed properties with fallback to localStorage
  const getProfileType = () => {
    if (profile?.profile_type) {
      return profile.profile_type;
    }
    // Fallback to localStorage
    return localStorage.getItem('profile_type');
  };

  const isDeveloper = getProfileType() === 'developer';
  const isRegularUser = getProfileType() !== 'developer';

  return {
    profile,
    isLoading,
    error,
    refreshProfile,
    isDeveloper,
    isRegularUser,
  };
};